/**
 * <p>Telephony repository device type identifiers aligned with {@code CTelephonyModelConst.iDEVICE_TYPE_XXX}
 * and the {@code pDEVICE_TYPES} array in {@code CTelephonyModelConst}.</p>
 */
export enum DeviceType {
    Unknown = -1,
    Trunk = 1,
    TrunkGateway = 2,
    ChatService = 3,
    EmailServer = 4,
    ExtensionOrEndpoint = 20,
    ExtensionOrEndpointTrunking = 21,
    ExtensionOrEndpointHospitality = 22,
    GroupSuper = 39,
    GroupCollective = 40,
    GroupLinear = 41,
    GroupCyclic = 42,
    GroupAcd = 43,
    GroupTrunk = 44,
    GroupVoicemail = 50,
    GroupPage = 55,
    GroupDepartment = 56,
    GroupSecurityGroup = 57,
    Park = 60,
    VoicemailApplication = 70,
    VoicemailAnnouncementHuntGroup = 71,
    VoicemailAnnouncementGeneral = 72,
    AutoAttendant = 73,
    MusicOnHold = 80,
    Conference = 90,
    PilotNumber = 100,
    DnisEntry = 110
}

/**
 * <p>String form of each numeric {@link DeviceType} id, used in legacy servlet and REST fields.</p>
 */
export enum DeviceTypeIdString {
    Unknown = '-1',
    Trunk = '1',
    TrunkGateway = '2',
    ChatService = '3',
    EmailServer = '4',
    ExtensionOrEndpoint = '20',
    ExtensionOrEndpointTrunking = '21',
    ExtensionOrEndpointHospitality = '22',
    GroupSuper = '39',
    GroupCollective = '40',
    GroupLinear = '41',
    GroupCyclic = '42',
    GroupAcd = '43',
    GroupTrunk = '44',
    GroupVoicemail = '50',
    GroupPage = '55',
    GroupDepartment = '56',
    GroupSecurityGroup = '57',
    Park = '60',
    VoicemailApplication = '70',
    VoicemailAnnouncementHuntGroup = '71',
    VoicemailAnnouncementGeneral = '72',
    AutoAttendant = '73',
    MusicOnHold = '80',
    Conference = '90',
    PilotNumber = '100',
    DnisEntry = '110'
}

/**
 * <p>Canonical backend {@code DeviceType} request/response strings from
 * {@code CWSConstants.sTAGVALUE_PROPERTY_DEVICEXXX_DEVICETYPE_*}.</p>
 */
export enum DeviceTypeApiString {
    Unknown = 'Unknown',
    Trunk = 'Trunk',
    TrunkGateway = 'Trunk Gateway',
    ChatService = 'Chat Service',
    EmailServer = 'E-Mail Server',
    ExtensionOrEndpoint = 'Extension',
    ExtensionOrEndpointTrunking = 'Extension (Trunking)',
    ExtensionOrEndpointHospitality = 'Extension (Hospitality)',
    GroupSuper = 'Super Group',
    GroupCollective = 'Group (Collective)',
    GroupLinear = 'Group (Linear)',
    GroupCyclic = 'Group (Cyclic)',
    GroupAcd = 'Group (ACD)',
    GroupTrunk = 'Group (Trunk)',
    GroupVoicemail = 'Group (Voicemail)',
    GroupPage = 'Group (Page)',
    GroupDepartment = 'Department',
    GroupSecurityGroup = 'Security Group',
    Park = 'Park',
    VoicemailApplication = 'Voicemail',
    VoicemailAnnouncementHuntGroup = 'Queue Announcement (HG)',
    VoicemailAnnouncementGeneral = 'Queue Announcement (General)',
    AutoAttendant = 'Auto Attendant',
    MusicOnHold = 'MOH',
    Conference = 'Conference',
    PilotNumber = 'Pilot Number',
    DnisEntry = 'DNIS',
    CallQueue = 'Call Queue'
}

/**
 * <p>Returns the canonical string device type id used in API and form fields.</p>
 *
 * @param deviceType - Numeric {@link DeviceType} member.
 * @returns Decimal string id (e.g. {@code "43"}).
 */
export const deviceTypeIdString = (deviceType: DeviceType): DeviceTypeIdString => {
    switch (deviceType) {
        case DeviceType.Unknown:
            return DeviceTypeIdString.Unknown
        case DeviceType.Trunk:
            return DeviceTypeIdString.Trunk
        case DeviceType.TrunkGateway:
            return DeviceTypeIdString.TrunkGateway
        case DeviceType.ChatService:
            return DeviceTypeIdString.ChatService
        case DeviceType.EmailServer:
            return DeviceTypeIdString.EmailServer
        case DeviceType.ExtensionOrEndpoint:
            return DeviceTypeIdString.ExtensionOrEndpoint
        case DeviceType.ExtensionOrEndpointTrunking:
            return DeviceTypeIdString.ExtensionOrEndpointTrunking
        case DeviceType.ExtensionOrEndpointHospitality:
            return DeviceTypeIdString.ExtensionOrEndpointHospitality
        case DeviceType.GroupSuper:
            return DeviceTypeIdString.GroupSuper
        case DeviceType.GroupCollective:
            return DeviceTypeIdString.GroupCollective
        case DeviceType.GroupLinear:
            return DeviceTypeIdString.GroupLinear
        case DeviceType.GroupCyclic:
            return DeviceTypeIdString.GroupCyclic
        case DeviceType.GroupAcd:
            return DeviceTypeIdString.GroupAcd
        case DeviceType.GroupTrunk:
            return DeviceTypeIdString.GroupTrunk
        case DeviceType.GroupVoicemail:
            return DeviceTypeIdString.GroupVoicemail
        case DeviceType.GroupPage:
            return DeviceTypeIdString.GroupPage
        case DeviceType.GroupDepartment:
            return DeviceTypeIdString.GroupDepartment
        case DeviceType.GroupSecurityGroup:
            return DeviceTypeIdString.GroupSecurityGroup
        case DeviceType.Park:
            return DeviceTypeIdString.Park
        case DeviceType.VoicemailApplication:
            return DeviceTypeIdString.VoicemailApplication
        case DeviceType.VoicemailAnnouncementHuntGroup:
            return DeviceTypeIdString.VoicemailAnnouncementHuntGroup
        case DeviceType.VoicemailAnnouncementGeneral:
            return DeviceTypeIdString.VoicemailAnnouncementGeneral
        case DeviceType.AutoAttendant:
            return DeviceTypeIdString.AutoAttendant
        case DeviceType.MusicOnHold:
            return DeviceTypeIdString.MusicOnHold
        case DeviceType.Conference:
            return DeviceTypeIdString.Conference
        case DeviceType.PilotNumber:
            return DeviceTypeIdString.PilotNumber
        case DeviceType.DnisEntry:
            return DeviceTypeIdString.DnisEntry
        default: {
            const exhaustiveCheck: never = deviceType
            return exhaustiveCheck
        }
    }
}

/**
 * <p>Ordered list matching {@code CTelephonyModelConst.pDEVICE_TYPES} for add-device UI, excluding
 * {@link DeviceType.Unknown}.</p>
 */
export const DEVICE_TYPES_MODAL_ADD_ORDER: readonly DeviceType[] = Object.freeze([
    DeviceType.Trunk,
    DeviceType.TrunkGateway,
    DeviceType.ChatService,
    DeviceType.EmailServer,
    DeviceType.ExtensionOrEndpoint,
    DeviceType.ExtensionOrEndpointTrunking,
    DeviceType.ExtensionOrEndpointHospitality,
    DeviceType.GroupSuper,
    DeviceType.GroupCollective,
    DeviceType.GroupLinear,
    DeviceType.GroupCyclic,
    DeviceType.GroupAcd,
    DeviceType.GroupTrunk,
    DeviceType.GroupVoicemail,
    DeviceType.GroupPage,
    DeviceType.Park,
    DeviceType.VoicemailApplication,
    DeviceType.VoicemailAnnouncementHuntGroup,
    DeviceType.VoicemailAnnouncementGeneral,
    DeviceType.AutoAttendant,
    DeviceType.MusicOnHold,
    DeviceType.Conference,
    DeviceType.PilotNumber,
    DeviceType.DnisEntry,
    DeviceType.GroupDepartment,
    DeviceType.GroupSecurityGroup
])

/**
 * <p>Extension-style device types ({@code iDEVICE_TYPE_EXTENSIONORENDPOINT*}).</p>
 */
export const DEVICE_TYPE_IDS_EXTENSIONS: readonly DeviceType[] = Object.freeze([
    DeviceType.ExtensionOrEndpoint,
    DeviceType.ExtensionOrEndpointTrunking,
    DeviceType.ExtensionOrEndpointHospitality
])

/**
 * <p>Trunk, gateway, and omni-style server types grouped with trunks in admin filters.</p>
 */
export const DEVICE_TYPE_IDS_TRUNKS_AND_RELATED: readonly DeviceType[] = Object.freeze([
    DeviceType.Trunk,
    DeviceType.TrunkGateway,
    DeviceType.ChatService,
    DeviceType.EmailServer
])

/**
 * <p>Core hunt-style group types used when filters combine collective/linear/cyclic/ACD/voicemail/page.</p>
 */
export const DEVICE_TYPE_IDS_STANDARD_HUNT_GROUPS: readonly DeviceType[] = Object.freeze([
    DeviceType.GroupCollective,
    DeviceType.GroupLinear,
    DeviceType.GroupCyclic,
    DeviceType.GroupAcd,
    DeviceType.GroupVoicemail,
    DeviceType.GroupPage
])

/**
 * <p>Types between {@code iDEVICE_TYPE_GROUP_MIN} and {@code iDEVICE_TYPE_GROUP_MAX} (inclusive).</p>
 */
export const DEVICE_TYPE_IDS_GROUP_MIN_TO_MAX: readonly DeviceType[] = Object.freeze([
    DeviceType.GroupCollective,
    DeviceType.GroupLinear,
    DeviceType.GroupCyclic,
    DeviceType.GroupAcd,
    DeviceType.GroupTrunk,
    DeviceType.GroupVoicemail,
    DeviceType.GroupPage
])

/**
 * <p>Types treated as repository groups for membership checks, matching {@code IsDeviceTypeGroup}.</p>
 */
export const DEVICE_TYPE_IDS_TREATED_AS_GROUP: readonly DeviceType[] = Object.freeze([
    DeviceType.GroupSuper,
    ...DEVICE_TYPE_IDS_GROUP_MIN_TO_MAX,
    DeviceType.GroupDepartment,
    DeviceType.GroupSecurityGroup
])

/**
 * <p>Upper bound of the extension / endpoint range ({@code iDEVICE_TYPE_EXTENSIONORENDPOINT_MAX}).</p>
 */
export const DEVICE_TYPE_EXTENSION_OR_ENDPOINT_MAX = 29

/** Upper bound of the extension / endpoint range as a decimal string for API filter usage. */
export const EXTENSION_OR_ENDPOINT_MAX: string = String(DEVICE_TYPE_EXTENSION_OR_ENDPOINT_MAX)

/**
 * <p>Highest repository group-related device type id ({@code iDEVICE_TYPE_GROUP_SECURITYGROUP}), matching
 * {@code CTelephonyModelConst.IsDeviceTypeGroup} together with super (39), core groups (40–55), and department (56).</p>
 */
export const GROUP_MAX: string = DeviceTypeIdString.GroupSecurityGroup

/** Device type filter list used to scope admin device list queries to extensions and groups. */
export const DEVICE_FILTERS: string[] = [
    DeviceTypeIdString.ExtensionOrEndpoint,
    DeviceTypeIdString.ExtensionOrEndpointTrunking,
    DeviceTypeIdString.ExtensionOrEndpointHospitality,
    EXTENSION_OR_ENDPOINT_MAX,
    DeviceTypeIdString.GroupCollective,
    DeviceTypeIdString.GroupLinear,
    DeviceTypeIdString.GroupCyclic,
    DeviceTypeIdString.GroupAcd,
    DeviceTypeIdString.GroupVoicemail,
    DeviceTypeIdString.GroupPage,
    DeviceTypeIdString.PilotNumber
]

/** Device type filter list covering all group-style device types including super and trunk groups. */
export const ALL_GROUPS: string[] = [
    DeviceTypeIdString.GroupSuper,
    DeviceTypeIdString.GroupCollective,
    DeviceTypeIdString.GroupLinear,
    DeviceTypeIdString.GroupCyclic,
    DeviceTypeIdString.GroupAcd,
    DeviceTypeIdString.GroupTrunk,
    DeviceTypeIdString.GroupVoicemail,
    DeviceTypeIdString.GroupPage
]

/** Device type filter list for super group, collective group, and the upper group boundary. */
export const GROUPS: string[] = [DeviceTypeIdString.GroupSuper, DeviceTypeIdString.GroupCollective, GROUP_MAX]

/** Device type filter list restricting results to extension and endpoint device types only. */
export const DEVICE_FILTER_EXTENSIONS_ONLY: string[] = [
    DeviceTypeIdString.ExtensionOrEndpoint,
    EXTENSION_OR_ENDPOINT_MAX
]

/** Device type filter list restricting results to ACD group device types only. */
export const DEVICE_FILTER_ACD_GROUPS: string[] = [DeviceTypeIdString.GroupAcd]

/** Device type filter list covering non-ACD group types (collective, cyclic through trunk, and security group boundary). */
export const DEVICE_FILTER_NON_ACD_GROUPS: string[] = [
    DeviceTypeIdString.GroupCollective,
    `${DeviceTypeIdString.GroupCyclic}:${DeviceTypeIdString.GroupTrunk}`,
    GROUP_MAX
]

/** Device type filter list matching any group type from collective up to the security group boundary. */
export const DEVICE_FILTER_ANY_GROUPS: string[] = [DeviceTypeIdString.GroupCollective, GROUP_MAX]

/** Device type filter list representing no device type constraint (empty filter). */
export const DEVICE_FILTER_NONE: string[] = []
